import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { OrderMode, OrderType } from '@/enums/OrderEnums';
import { AlertType } from '@/interfaces/IAlert';
import { i18n } from '@/plugins/i18n';
import { getPosOrderTypeLabel } from '@/domain/pos/order-type-ui';
import { resolveAllowedOrderType } from '@/domain/pos/order-type-policies';
import { usePosNavigation } from './use-pos-navigation';
import { useUi } from './use-ui';
import {
  isEmptyUneditedDraft,
  usePosDraftsStore,
} from '@/stores/pos-drafts.store';
import { useRestaurantStore } from '@/stores/restaurant.store';

/**
 * "New order" entry point shared by the POS V2 header button and the mobile
 * bottom bar. Guards mirror PosV2OrderTabs.handleAddDraft; the empty-unedited
 * active tab is reused so the action never spams empty tabs.
 */
export function usePosNewOrder() {
  const restaurantStore = useRestaurantStore();
  const posDrafts = usePosDraftsStore();
  const posNavigation = usePosNavigation();
  const route = useRoute();
  const router = useRouter();
  const ui = useUi();

  const isWhatsAppMode = computed(
    () => restaurantStore.selectedBranch?.ordersMode === OrderMode.WhatsApp
  );

  function navigateToNewOrder(preferred: OrderType = OrderType.Seat): void {
    if (isWhatsAppMode.value) return;

    const branch = restaurantStore.selectedBranch;
    if (branch?.allowOrders === false) {
      ui.showToast(
        AlertType.Info,
        String(i18n.t('posV2.branchNotAcceptingOrders'))
      );
      return;
    }

    const type = resolveAllowedOrderType(
      {
        tableOrders: branch?.tableOrders ?? null,
        takeAway: branch?.takeAway ?? null,
        delivery: branch?.delivery ?? null,
      },
      preferred
    );
    if (!type) {
      ui.showToast(AlertType.Info, String(i18n.t('posV2.flowDisabled')));
      return;
    }
    if (type !== preferred) {
      ui.showToast(
        AlertType.Info,
        String(
          i18n.t('posV2.typeFallbackToast', {
            preferred: getPosOrderTypeLabel(preferred),
            type: getPosOrderTypeLabel(type),
          })
        )
      );
    }

    // Prefer reusing an empty, unedited "create" tab so we don't spam tabs.
    const active = posDrafts.activeDraft;
    if (isEmptyUneditedDraft(active)) {
      if (active.type !== type) {
        posDrafts.updateDraft(active.id, { type, isDirty: false });
      }
      posDrafts.setActiveDraft(active.id);
    } else {
      posDrafts.createDraft(type);
    }

    posNavigation.rememberOrderWorkspaceOrigin(route.fullPath);
    router.push({ name: 'restaurantPosOrder' }).catch(() => undefined);
  }

  return { isWhatsAppMode, navigateToNewOrder };
}
